import { EnrollmentStatus } from '@zorro/clients';
import { Button } from '@zorro/design-system/v2/Button';
import { Drawer } from '@zorro/design-system/v2/Drawer';
import { useMonolithQuery } from '@zorro/web/ui-utils';
import { useState } from 'react';
import { $enum } from 'ts-enum-util';

import { useAnalytics } from '../../../Analytics';
import { ReportLifeEventForm } from '../../../EmployeeActions/EnrollmentActions/ReportLifeEventForm';

const getReportLifeEventEnabled = (enrollmentStatus: EnrollmentStatus) =>
  $enum.mapValue(enrollmentStatus).with<boolean>({
    [EnrollmentStatus.ELECTION_SUBMITTED]: true,
    [EnrollmentStatus.WAIVED_ELECTION]: true,
    [EnrollmentStatus.ENROLLMENT_IN_PROCESS]: true,
    [EnrollmentStatus.ENROLLMENT_CONFIRMED]: true,
    [EnrollmentStatus.ACTIVE_COVERAGE]: true,
    [EnrollmentStatus.WAIVED_COVERAGE]: true,
    [EnrollmentStatus.PENDING_ELECTION_WINDOW]: false,
    [EnrollmentStatus.ELECTION_ACTIVE]: false,
    [EnrollmentStatus.ELECTION_ACTIVE_HAS_NOT_STARTED]: false,
    [EnrollmentStatus.ELECTION_ACTIVE_STARTED]: false,
    [EnrollmentStatus.DEADLINE_PASSED]: false,
    [EnrollmentStatus.COVERAGE_ENDED]: false,
    [EnrollmentStatus.NO_ENROLLMENTS]: false,
  });

type Props = {
  employeeId: string;
  onboardingPeriodId: string;
  onSuccess?: (onboardingPeriodId?: string) => void;
};

export const ReportLifeEventButton = ({
  employeeId,
  onboardingPeriodId,
  onSuccess,
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const { sendEvent } = useAnalytics();

  const { data: onboardingPeriod } = useMonolithQuery({
    method: 'onboardingPeriodReportsControllerGenerateOnboardingPeriodReport',
    params: [onboardingPeriodId],
  });

  const { data: employee } = useMonolithQuery({
    method: 'employeesControllerFindOne',
    params: [employeeId],
  });

  if (!onboardingPeriod || !employee) {
    return null;
  }

  if (!getReportLifeEventEnabled(onboardingPeriod.enrollmentStatus)) {
    return null;
  }

  const handleClick = () => {
    void sendEvent('click_report_life_event', {
      onboarding_period_id: onboardingPeriodId,
      page: 'enrollment_details-admin',
    });
    setIsOpen(true);
  };

  const handleSuccess = (onboardingPeriodId?: string) => {
    setIsOpen(false);
    onSuccess?.(onboardingPeriodId);
  };

  return (
    <>
      <Button size="md" variant="secondary" onClick={handleClick}>
        Report life event
      </Button>
      <Drawer
        opened={isOpen}
        title="Report a Qualifying Life Event"
        onClose={() => setIsOpen(false)}
      >
        <ReportLifeEventForm
          employee={employee}
          onboardingPeriod={onboardingPeriod}
          onSuccess={handleSuccess}
        />
      </Drawer>
    </>
  );
};
